import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Select from "react-select";


import PageTitle from "../../components/PageTitle";
import './software.css';

import { Softlist } from "./list";

const sortOptions = [
	{ value: 'newest', label: '新しい順' },
	{ value: 'oldest', label: '古い順' },
	{ value: 'titleAsc', label: '名前順 (昇順)' },
	{ value: 'titleDesc', label: '名前順 (降順)' },
];

interface TagOption {
	value: string;
	label: string;
	count?: number;
}

interface TagGroup {
	label: string;
	options: TagOption[];
}

// 非表示を解除するための隠しキーワード
const UNLOCK_KEYWORD = ["ラブリン＝ラブリっち", "オタンコナス", "黒歴史","変身","あなたの全てを見せて","あなたはどこにいるの","いちのせ","おうちのでかたまごっち","でかたま主人公"];

// ソフトウェアタグのグループ定義
const SOFTWARE_TAG_GROUPS = [
	{ label: '媒体', tags: ['ゲーム', '映像作品'] },
	{ label: 'ボリューム', tags: ['短編', '長編'] },
	{ label: 'ソート', tags: ['不気味', '静かな狂気', '皮肉', '軽度のジャンプスケア', 'SNS風'] },
];



const Software = () => {
	// ページ番号を sessionStorage から復元（戻ってきた時に元のページを表示するため）
	const [currentPage, setCurrentPage] = useState(() => {
		const saved = sessionStorage.getItem('software-last-page');
		return saved ? parseInt(saved) : 1;
	});
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedYear, setSelectedYear] = useState<string | number>("All");
	const [sortType, setSortType] = useState("newest");
	const [currentTags, setCurrentTags] = useState<TagOption[]>([]);
	const location = useLocation();
	const navigate = useNavigate();

	// localStorage から表示フラグを取得（初期化）
	const [showHidden, setShowHidden] = useState(() => {
		return localStorage.getItem('show_hidden_software') === 'true';
	});

	const itemsPerPage = 6;

	// 利用可能な年を抽出
	const availableYears = useMemo(() => {
		const visibleSofts = Softlist.filter(s => showHidden || s.flags !== "true");
		const years = visibleSofts.map(soft => soft.update ? new Date(soft.update).getFullYear() : null).filter(Boolean);
		return Array.from(new Set(years)).sort((a, b) => b! - a!);
	}, [showHidden]);

	// タグ一覧と各タグの該当件数を抽出 (検索ワードや年に連動して変動するように修正)
	const tagOptions: TagGroup[] = useMemo(() => {
		// 1. まず表示可能な全タグのリストを作成（(0)件でも表示を維持するため）
		const allVisibleTags = new Set<string>();
		Softlist.forEach(soft => {
			if (showHidden || soft.flags !== "true") {
				soft.tags?.forEach(t => allVisibleTags.add(t));
			}
		});

		// タグごとの件数計算ヘルパー
		const getTagOption = (tag: string): TagOption => {
			const count = Softlist.filter(soft => {
				if (!showHidden && soft.flags === "true") return false;

				const query = searchTerm.toLowerCase();
				const matchSearch = soft.title.toLowerCase().includes(query);
				const softYear = soft.update ? new Date(soft.update).getFullYear() : null;
				const matchYear = selectedYear === "All" || softYear === selectedYear;

				// 現在選択中の「他の」タグとの整合性を確認
				const otherTags = currentTags.filter(t => t.value !== tag);
				const matchOtherTags = otherTags.every(t => soft.tags?.includes(t.value));
				const matchThisTag = soft.tags?.includes(tag);

				return matchSearch && matchYear && matchOtherTags && matchThisTag;
			}).length;
			return { value: tag, label: tag, count };
		};

		// 2. グループ定義に従って構造化
		const definedTags = new Set(SOFTWARE_TAG_GROUPS.flatMap(g => g.tags));
		
		const groupedOptions: TagGroup[] = SOFTWARE_TAG_GROUPS.map(group => ({
			label: group.label,
			options: group.tags
				.filter(t => allVisibleTags.has(t))
				.map(t => getTagOption(t))
				.sort((a, b) => (b.count || 0) - (a.count || 0) || a.label.localeCompare(b.label))
		}));

		// 定義に含まれていないタグを「その他」としてまとめる
		const otherTags = Array.from(allVisibleTags)
			.filter(t => !definedTags.has(t))
			.map(t => getTagOption(t))
			.sort((a, b) => (b.count || 0) - (a.count || 0) || a.label.localeCompare(b.label));

		if (otherTags.length > 0) {
			groupedOptions.push({ label: 'その他', options: otherTags });
		}

		return groupedOptions;
	}, [showHidden, searchTerm, selectedYear, currentTags]);

	// フィルタリングとソートの統合ロジック
	const filteredAndSortedItems = useMemo(() => {
		let result = Softlist.filter(soft => {
			// ロック解除されていない、かつ特殊フラグが "true" の場合は除外
			if (!showHidden && soft.flags === "true") return false;

			const query = searchTerm.toLowerCase();
			const matchSearch = soft.title.toLowerCase().includes(query);
			
			const softYear = soft.update ? new Date(soft.update).getFullYear() : null;
			const matchYear = selectedYear === "All" || softYear === selectedYear;

			const matchTags = currentTags.length === 0 || 
				currentTags.every(tag => (soft as any).tags?.includes(tag.value));

			return matchSearch && matchYear && matchTags;
		});

		return result.sort((a, b) => {
			if (sortType === 'titleAsc') return a.title.localeCompare(b.title, 'ja');
			if (sortType === 'titleDesc') return b.title.localeCompare(a.title, 'ja');
			
			const timeA = a.update ? new Date(a.update).getTime() : 0;
			const timeB = b.update ? new Date(b.update).getTime() : 0;
			return sortType === 'oldest' ? timeA - timeB : timeB - timeA;
		});
	}, [searchTerm, selectedYear, currentTags, sortType, showHidden]);

	const totalPages = Math.max(1, Math.ceil(filteredAndSortedItems.length / itemsPerPage));
	const currentItems = filteredAndSortedItems.slice(
		(currentPage - 1) * itemsPerPage,
		currentPage * itemsPerPage
	);

	// 他のページ（詳細ページなど）からタグ指定で遷移してきた場合の処理
	useEffect(() => {
		const state = location.state as { filterTag?: string };
		if (state?.filterTag) {
			const tagValue = state.filterTag;
			// TagOptionオブジェクトを直接生成（tagOptionsへの依存を排除してループを防止）
			setCurrentTags([{ value: tagValue, label: tagValue }]);
			setCurrentPage(1);
			setSearchTerm("");
			setSelectedYear("All");

			// React Routerのnavigateを使用してstateをクリア（ループの根本原因を解消）
			navigate(location.pathname, { replace: true, state: {} });

			// タグから戻った時は検索結果が見える位置までスクロール
			setTimeout(() => {
				const searchElement = document.querySelector('.Searchflame');
				searchElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}, 100);
		}
	}, [location.state, location.pathname, navigate]);

	// 特定のタイルへのスクロール復元処理
	useEffect(() => {
		const targetId = sessionStorage.getItem('software-scroll-target');
		if (targetId && currentItems.length > 0) {
			// レンダリングとアニメーションの完了を少し待ってから実行
			const timer = setTimeout(() => {
				const el = document.getElementById(`soft-card-${targetId}`);
				if (el) {
					el.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}
				// 実行後はクリア
				sessionStorage.removeItem('software-scroll-target');
				sessionStorage.removeItem('software-last-page');
			}, 600);
			return () => clearTimeout(timer);
		}
	}, [currentItems]);

	// キーワード監視：隠しキーワードが入力されたらフラグを立てて保存
	useEffect(() => {
		const allOptions = tagOptions.flatMap(g => g.options);
		const isAllTagsSelected = currentTags.length > 0 && currentTags.length === allOptions.length;
		
		if (isAllTagsSelected && !showHidden && UNLOCK_KEYWORD.includes(searchTerm)) {
			let messages: string[] = [];

			if (searchTerm === "ラブリン＝ラブリっち") {
				messages = [
					"随分と懐かしい名前を知っていますね。",
					"どうやってこの名前を知ったのかは分かりません。\n…私の前世はここだけのナイショですよ？",
					"そんなあなたへ、捧げます"
				];
			} else if (searchTerm === "オタンコナス") {
				messages = [
					"随分と懐かしい名前を知っていますね。",
					"どうやってこの名前を知ったのかは分かりません。\n…私の前世はここだけのナイショですよ？",
					"そんなあなたへ、捧げます"
				];
			} else if (searchTerm === "いちのせ") {
				messages = [
					"…あー(悶絶)",
					"でかたま主人公の二次創作でよく呼ばれていた名前ですね。\n懐かしいです。",
					"…というかこんなパターンを何度も試されちゃ私狂っちゃいますよ…w\nやめて下さいな…\n(SNSに公開はご遠慮下さいネ)"
				];
			} else if (searchTerm === "おうちのでかたまごっち") {
				messages = [
					"2014年当時は持ってませんでした。\n私なんかよりよく研究してる方がいますね。",
					"人間がたまごっちになっちゃう、公式本体でそれをやる。\nそんなロマンがありましたね。",
					"それにガチっていた頃に作ったゲームを捧げます。\n(SNSに公開はご遠慮下さいネ)"
				];
			} else if (searchTerm === "でかたま主人公") {
				messages = [
					"Uniで再登場しましたね！",
					"最近のBANDAIの動向が読めませんね。\nいやはや本当に。",
					"それとは関係は全く無いんですが、\n隠してたゲームを解禁したのでよろしければ。\n(SNSに公開はご遠慮下さいネ)"
				];
			
			} else {
				messages = [
					"隠しゲームを解放しました。",
					"皆さんにはナイショですよ？\n(SNSに公開はご遠慮下さいネ)"
				];
			}

			const timer = setTimeout(() => {
				setShowHidden(true);
				localStorage.setItem('show_hidden_software', 'true');
				messages.forEach(msg => alert(msg));
				setCurrentTags([]);
				setSearchTerm("T F");
			}, 2000);

			return () => clearTimeout(timer);
		}
	}, [searchTerm, showHidden, currentTags, tagOptions]);

	// 検索条件が変わったら1ページ目に戻す
	const handleFilterChange = (setter: any, value: any) => {
		setter(value);
		setCurrentPage(1);
	};

	return (
		<div>
			{/* とりあえず骨組みだけ */}
			<div className="software-page-wrapper" />
			<PageTitle title={"Software"} />

			<h1 className="top-title">Software</h1>

			<div className="top-message-box">
				<p>基本的にWindows専用です。Mac/Linux等のOSではWine等で動くかもしれません。</p>
				<p>また、古い作品は最新の環境で正常に動作しない可能性があります。</p>
				<p>ご了承ください。</p>
			</div>


			{/* ここからソフト */}
			<div className="Searchflame">
				{/* 年選択 */}
				<select 
					className='QuickYearMenu'
					value={selectedYear}
					onChange={(e) => {
						const val = e.target.value === "All" ? "All" : Number(e.target.value);
						handleFilterChange(setSelectedYear, val);
					}}
				>
					<option value="All">すべての年</option>
					{availableYears.map(year => (
						<option key={year} value={year!}>{year}年</option>
					))}
				</select>

				{/* ソート */}
				<select className='SortMenu' value={sortType} onChange={(e) => handleFilterChange(setSortType, e.target.value)}>
					{sortOptions.map(opt => (
						<option key={opt.value} value={opt.value}>{opt.label}</option>
					))}
				</select>

				{/* キーワード検索 */}
				<input
					className="QuickSearchMenu"
					type="text"
					placeholder="キーワード検索"
					value={searchTerm}
					onChange={(e) => handleFilterChange(setSearchTerm, e.target.value)}
				/>

				{/* タグ検索 (react-select) */}
				<div className="TagSearchMenu" style={{ width: '100%', boxSizing: 'border-box', marginTop: '10px' }}>
					<Select<TagOption, true, TagGroup>
						isMulti
						options={tagOptions as any}
						value={currentTags}
						onChange={(newValue) => handleFilterChange(setCurrentTags, newValue ? [...newValue] : [])}
						placeholder="ソート / フィルタ"
						formatOptionLabel={({ label, count }, { context }) => 
							context === 'menu' ? `${label} (${count})` : label
						}
						styles={{
							control: (base) => ({
								...base,
								width: '100%',
								boxSizing: 'border-box',
								minHeight: '40px',
								fontSize: '18px',
								borderRadius: '10px',
							}),
							menuPortal: (base) => ({ ...base, zIndex: 9999 }),
							placeholder: (base) => ({
								...base,
								color: '#888', // Gallery.tsxと統一
							})
						}}
						menuPortalTarget={document.body}
					/>
				</div>
			</div>

			<div className="software-list-container">
				

				{/* ページネーション (上部) - 検索結果がある場合のみ表示 */}
				{filteredAndSortedItems.length > 0 && (
					<div className="pagination-controls" style={{ padding: '40px 0', textAlign: 'center' }}>
						<button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
							{"<<"}
						</button>
						<button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
							{"<"}
						</button>
						<span className="page-indicator" style={{ color: 'white', margin: '0 15px' }}>{currentPage} / {totalPages}</span>
						<button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>
							{">"}
						</button>
						<button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
							{">>"}
						</button>
					</div>
				)}

				<div className="software-grid">
					<AnimatePresence mode="wait">
						{currentItems.length > 0 ? (
							currentItems.map((soft, index) => (
								<Link 
									key={`${soft.id}-${index}`} 
									to={`/software/${soft.id}`} 
									style={{ textDecoration: 'none' }}
									onClick={() => {
										sessionStorage.setItem('software-scroll-target', soft.id);
										sessionStorage.setItem('software-last-page', currentPage.toString());
									}}
								>
									<motion.div 
										id={`soft-card-${soft.id}`}
										initial={{ opacity: 0, y: 10 }}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0 }}
										whileHover={{ opacity: 0.7, scale: 0.97 }}
										className="software-item-card"
									>
										<img 
											src={soft.img} 
											alt={soft.title} 
											className="software-thumbnail" 
											loading="lazy"
											decoding="async"
										/>
										<p className="software-name">
											{soft.title}
										</p>
									</motion.div>
								</Link>
							))
						) : (
							<motion.div
								key="not-found"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								className='NotApplicable'
							>
								該当するモノが見つかりませんでした。<br />
								検索条件を変えてみて下さい。
							</motion.div>
						)}
					</AnimatePresence>
				</div>

				{/* ページネーション (下部) */}
				{filteredAndSortedItems.length > 0 && (
					<div className="pagination-controls" style={{ padding: '40px 0', textAlign: 'center' }}>
					<button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
						{"<<"}
					</button>
					<button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
						{"<"}
					</button>
					<span className="page-indicator" style={{ color: 'white', margin: '0 15px' }}>{currentPage} / {totalPages}</span>
					<button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>
						{">"}
					</button>
					<button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
						{">>"}
					</button>
					</div>
				)}
			</div>


		</div>
	);


}
export default Software;